import { Trans, useTranslation } from 'next-i18next';
import { type SchoolSearchRes } from '@/api';
import { ErrorBoundaryFallback } from '@/components';
import { LoadingImage } from '@/components/Loading';
import { useInfiniteScrollEvent } from '@/hooks/useInfiniteScrollEvent';
import { LoadingSpinner } from '@/shared/components';
import { cn } from '@/utils';
import { useSchoolSearch } from '../../useSchoolSearch';
interface SchoolSearchResultAreaProps {
  submittedQuery: string;
  onSelectSchool?: (school: SchoolSearchRes) => void;
}
export const SchoolSearchResultArea = ({
  submittedQuery,
  onSelectSchool
}: SchoolSearchResultAreaProps) => {
  const {
    schools,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError
  } = useSchoolSearch(submittedQuery, submittedQuery.length > 0);
  const scrollRef = useInfiniteScrollEvent({
    hasNextPage,
    fetchNextPage
  });
  const renderContent = () => {
    if (isLoading) {
      return <LoadingState />;
    }
    if (isError) {
      return <ErrorState />;
    }
    if (schools.length === 0) {
      return <EmptyState />;
    }
    return <SuccessState schools={schools} onSelectSchool={onSelectSchool ?? (() => {})} isFetchingNextPage={isFetchingNextPage} data-sentry-element="SuccessState" data-sentry-component="renderContent" data-sentry-source-file="SchoolSearchResultArea.tsx" />;
  };
  return <div className="flex flex-col gap-xs" data-sentry-component="SchoolSearchResultArea" data-sentry-source-file="SchoolSearchResultArea.tsx">
      <div ref={scrollRef} className="max-h-[400px] overflow-y-auto pb-lg pt-3xs">
        {renderContent()}
      </div>
    </div>;
};

// ============================================================
// 검색 결과 상태별 컴포넌트 분리
// ============================================================
const LoadingState = () => {
  return <div className="flex justify-center" data-sentry-component="LoadingState" data-sentry-source-file="SchoolSearchResultArea.tsx">
      <LoadingImage className="size-[40px]" data-sentry-element="LoadingImage" data-sentry-source-file="SchoolSearchResultArea.tsx" />
    </div>;
};
const ErrorState = () => {
  return <div data-sentry-component="ErrorState" data-sentry-source-file="SchoolSearchResultArea.tsx">
      <ErrorBoundaryFallback data-sentry-element="ErrorBoundaryFallback" data-sentry-source-file="SchoolSearchResultArea.tsx" />
    </div>;
};
const EmptyState = () => {
  const {
    t
  } = useTranslation(['common']);
  return <div className="flex flex-col text-body-lg-medium text-assistive" data-sentry-component="EmptyState" data-sentry-source-file="SchoolSearchResultArea.tsx">
      <Trans t={t} i18nKey="signup.school.notFound" components={{
      br: <br />
    }} data-sentry-element="Trans" data-sentry-source-file="SchoolSearchResultArea.tsx" />
    </div>;
};
const SuccessState = ({
  schools,
  onSelectSchool,
  isFetchingNextPage
}: {
  schools: SchoolSearchRes[];
  onSelectSchool: (school: SchoolSearchRes) => void;
  isFetchingNextPage: boolean;
}) => {
  return <div className="flex flex-col gap-xs" data-sentry-component="SuccessState" data-sentry-source-file="SchoolSearchResultArea.tsx">
      {schools.map((school, index) => <SchoolItem key={`${school.standardCode}-${index}`} school={school} onClick={() => onSelectSchool(school)} />)}
      {isFetchingNextPage && <div className="flex items-center justify-center py-md">
          <LoadingSpinner className="size-4 text-gray-500" />
        </div>}
    </div>;
};
const SchoolItem = ({
  school,
  onClick
}: {
  school: SchoolSearchRes;
  onClick: () => void;
}) => {
  return <div onClick={onClick} className={cn('flex flex-col gap-3xs py-3xs items-start cursor-pointer group')} data-sentry-component="SchoolItem" data-sentry-source-file="SchoolSearchResultArea.tsx">
      <div className="text-body-lg-semibold group-hover:text-strong">
        {school.name}
      </div>
      <div className="text-body-sm-medium text-assistive">
        {school.roadAddress}, {school.roadZip}
      </div>
    </div>;
};